import React, {Suspense, useEffect, useState} from "react";
import {makeStyles} from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import {ErrorScreen, FuseAnimate, HeaderToolbar, Loading} from "../../components";
import Toolbar from "@material-ui/core/Toolbar";
import Card from "@material-ui/core/Card";
import {useAuthUser} from "../../components/auth-provider";
import {Controller, useForm} from "react-hook-form";
import {UserService} from "../../services";
import Typography from "@material-ui/core/Typography";
import {Button} from "@material-ui/core";
import PasswordStrengthMeter from "../../components/password-strength-meter/password-strength-meter";
import NewPasswordField from "../../components/new-password-field/new-password-field";
import {CheckCircleOutline} from "@material-ui/icons";
import {Link} from "react-router-dom";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        marginTop: 16,
        justifyContent: "center",
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        maxWidth: 340,
        [theme.breakpoints.up('sm')]: {
            maxWidth: 360,
        },
    },
}));


const PasswordPage = ({location}) => {
    const classes = useStyles();
    const {user} = useAuthUser();          // Get logged in user
    const [state, setState] = useState({success: false, error: null})

    const {handleSubmit, control, formState:{isValid, isSubmitting}} = useForm({ mode: 'onChange' });
    const [scoreState, setScoreState] = useState({pwCheck:{
            score      : 0,
            rating     : '',
            warning    : '',
            suggestions: [
                'Use a few words, avoid common phrases',
                'No need for symbols, digits, or uppercase letters',
            ],
        },
    })

    //console.log('user', user)


    // Cache the password score so we can update it for password validation in the same render cycle
    let pwScore = scoreState.pwCheck.score

    // onSubmit is called by ReactHookForm when the form is submitted and valid.
    // It returns a promise that is used by RHF to set the isSubmitting flag.
    const onSubmit = (data) => {
        return new Promise((resolve) => {
            UserService.setPassword(user.accountId, user.id, data.password)
                .then(() => {
                    // password has been updated - display a success message
                    setState({...state, success: true})
                })
                .catch(error => {
                    // Display an API error message
                    setState({...state, error})
                })
                .finally(() => {
                    resolve() // Resolve the promise so RHF knows we're done.
                })
        })
    }

    const handlePasswordChange = (event, check, onChange) => {
        //console.log('handlePasswordChange check', check)
        setScoreState({...scoreState, pwCheck: check})
        pwScore = check.score
        return onChange(event)
    }

    const isValidPassword = (value) => {
        //console.log('isValidPassword',value, pwScore)
        return pwScore > 2
    }

    // Scroll to top on mount
    useEffect( () => {
        document.getElementById('psb').scrollTo(0,0);
    }, [])  // <- empty deps means it runs only on load

    // Make sure the account is available in the store
    // useEffect(()=> {
    //     dispatch(fetchAccount(user.accountId, store, dispatch))
    // }, [user.accountId, store, dispatch])

    return (
        <div id="requests" className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location={location.pathname}/>
            </AppBar>
            <Toolbar/>

            <main className={classes.content}>
                { state.error && <ErrorScreen error={state.error}/> }
                { state.error === null && !state.success ?
                    <div className="flex flex-grow flex-col items-center">
                        <Card className={classes.card}>
                            <FuseAnimate animation="transition.expandIn" delay={200}>
                                <div id="change-password" className="my-4">
                                    <Typography variant="h6" color="secondary">Change Password</Typography>
                                    <form
                                        name="passwordForm"
                                        noValidate
                                        className="flex flex-col justify-center w-full mt-2"
                                        onSubmit={handleSubmit(onSubmit)}
                                    >
                                        <Suspense fallback={<Loading/>}>
                                            <Controller
                                                name="password"
                                                control={control}
                                                defaultValue=""
                                                rules={{
                                                    required: 'A password is required',
                                                    validate: (value) => isValidPassword(value) || 'A strong password is required'
                                                }}
                                                render={ ({field: {onChange, value}, fieldState:{error} }) => (
                                                    <>
                                                        <NewPasswordField
                                                            label="Password"
                                                            error={!!error}
                                                            helperText={error ? error.message : 'Password Strength'}
                                                            password={value}
                                                            onChange={(event, check) => handlePasswordChange(event, check, onChange)}
                                                            exclusions={['prisonconnect','telephoneer','phonethru']} // exclude from passwords
                                                        />
                                                        <PasswordStrengthMeter check={scoreState.pwCheck} variant="full"/>
                                                    </>
                                                )}
                                            />
                                        </Suspense>

                                        <div className="mt-2">
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                color="primary"
                                                aria-label="CHANGE PASSWORD"
                                                className="w-full"
                                                disabled={!isValid || isSubmitting}
                                            >
                                                {isSubmitting ? 'Changing password...' : 'Change Password'}
                                            </Button>
                                        </div>

                                    </form>
                                </div>
                            </FuseAnimate>
                        </Card>
                    </div>
                    : null
                }
                { state.success ?
                    <div className="flex flex-grow flex-col items-center">
                        <Card className={classes.card}>
                            <FuseAnimate animation="transition.expandIn" delay={200}>
                                <SuccessPanel />
                            </FuseAnimate>
                        </Card>
                    </div>
                    : null }
            </main>
        </div>
    )
}

const SuccessPanel = () => {

    return (
        <div className="flex flex-col items-center w-full">
            <FuseAnimate animation="callout.bounce" delay={400}>
                <div className="h-16 my-6">
                    <CheckCircleOutline style={{fontSize: 64, color: 'green'}} />
                </div>
            </FuseAnimate>
            <FuseAnimate animation="transition.expandIn" delay={100}>
                <Typography variant="body1" align="left" paragraph>
                    Your password has been updated!
                </Typography>
            </FuseAnimate>
            <div className="mt-4 w-full">
                <Button variant="contained" color="primary" aria-label="OK" className="w-full" component={Link} to="/account">
                    OK
                </Button>
            </div>
        </div>
    )
}

export default PasswordPage;